import { useState, useEffect, useMemo, useCallback } from "react";
import { Plus, ArrowDownCircle, ArrowUpCircle, AlertTriangle, Calendar, DollarSign, TrendingUp, Wallet, Paperclip as AttachmentIcon, MoreVertical, Edit, Trash2, Check, Upload, FileText, Receipt, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { MainLayout } from "@/components/MainLayout";
import { useTransactions } from "@/hooks/useTransactions";
import { useTransactionFilters } from "@/hooks/useTransactionFilters";
import { TransactionFormContainer } from "@/components/transactions/TransactionFormContainer";
import { TransactionSummaryCards } from "@/components/transactions/TransactionSummaryCards";
import { OptimizedTransactionList } from "@/components/transactions/OptimizedTransactionList";
import { format, isAfter, isBefore, startOfDay, endOfDay, addDays } from "date-fns";
import { ptBR } from "date-fns/locale";
import { formatBR, fromDateString } from "@/lib/utils/dateUtils";
import { SEO } from "@/components/SEO";
import { toast } from "sonner";
import { useParams } from "react-router-dom";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { StatusPill } from "@/components/ui/StatusPill";
import { SortableHeader } from "@/components/ui/SortableHeader";
import { useAttachmentsSync } from "@/hooks/useAttachmentsSync";
import { VencimentoDateFilter } from "@/components/shared/VencimentoDateFilter";
import { BatchActionsDropdown } from "@/components/transactions/BatchActionsDropdown";
import { BatchDatePickerModal } from "@/components/transactions/BatchDatePickerModal";
import { BatchAmountModal } from "@/components/transactions/BatchAmountModal";
import { BatchAccountSelectModal } from "@/components/transactions/BatchAccountSelectModal";
import { ImportSpreadsheetDialog } from "@/components/transactions/ImportSpreadsheetDialog";
import { RecurringIndicator } from "@/components/transactions/RecurringIndicator";
import { shouldDisplaySequence } from "@/lib/utils/recurrenceSequence";
import { ReceiptModal } from "@/components/receipts/ReceiptModal";
import { BillModal } from "@/components/billing/BillModal";
import {
  calculateTransactionStatus,
  getTransactionStatusVariant,
  getTransactionStatusText,
  updateTransactionStatus
} from "@/utils/transactionStatusUtils";


interface GridTransactionRowProps {
  transaction: any;
  isSelected: boolean;
  onSelect: (id: string) => void;
  onEdit: (id: string) => void;
  onTogglePaid: (id: string) => Promise<void> | void;
  onDelete: (id: string) => Promise<void> | void;
  onClone: (tx: any) => void;
  onGenerateReceipt: (tx: any) => void;
  onGenerateBill: (tx: any) => void;
  allTransactions?: any[];
}

function GridTransactionRow({
  transaction,
  isSelected,
  onSelect,
  onEdit,
  onTogglePaid,
  onDelete,
 onClone,
  onGenerateReceipt,
  onGenerateBill,
  allTransactions,
}: GridTransactionRowProps) {
  const { useTransactionAttachmentsQuery, hasAttachments } = useAttachmentsSync();
  const { data: attachments = [], isLoading: isLoadingAttachments } = useTransactionAttachmentsQuery(transaction.id);
  const hasAttachmentsOptimistic = (hasAttachments(transaction.id) || attachments.length > 0) && !isLoadingAttachments;

  const handleRowClick = () => onEdit(transaction.id);
  const stop = (e: React.MouseEvent) => e.stopPropagation();

  return (
    <div
      onClick={handleRowClick}
      className="grid gap-2 px-4 py-3 cursor-pointer transition-all duration-200 bg-muted/30 hover:bg-accent/10 hover:border-primary/50 hover:shadow-md grid-cols-transaction-row transaction-row"
    >
      {/* Checkbox - prevent click propagation */}
      <div className="flex items-center justify-center">
        <Checkbox
          checked={isSelected}
          onCheckedChange={() => onSelect(transaction.id)}
          onClick={stop}
          className="cursor-pointer"
        />
      </div>

      {/* Status */}
      <div className="flex items-center min-w-0">
        <StatusPill
          variant={getTransactionStatusVariant(transaction)}
          size="md"
        >
          {getTransactionStatusText(transaction)}
        </StatusPill>
      </div>

      {/* Data */}
      <div className="flex flex-col items-start text-sm min-w-[120px]">
        <>
          <div className="flex items-center gap-1">
            <Calendar className="h-3 w-3 text-muted-foreground flex-shrink-0" />
            <span className="text-muted-foreground text-xs whitespace-nowrap">Venc:</span>
            <span className="font-medium text-xs whitespace-nowrap text-foreground">
              {formatBR(transaction.due_date || transaction.date || transaction.payment_date)}
            </span>
          </div>
          <div className="flex items-center gap-1 mt-0.5">
            <Check className="h-3 w-3 flex-shrink-0 text-muted-foreground" />
            <span className="text-xs whitespace-nowrap text-muted-foreground">Pag.:</span>
            <span className="text-xs whitespace-nowrap text-muted-foreground">
              {transaction.date ? formatBR(transaction.date) : '—'}
            </span>
          </div>
        </>
      </div>

      {/* Descrição + Categoria + Fornecedor/Cliente */}
      <div className="flex flex-col justify-center items-start min-w-0">
        <div className="font-medium text-sm truncate w-full text-left flex items-center gap-2">
          {transaction.description}
          {shouldDisplaySequence(transaction, allTransactions) && (
            <RecurringIndicator
              transaction={transaction}
              variant="sequence"
              className="ml-1 text-xs text-muted-foreground flex-shrink-0"
              allTransactions={allTransactions}
            />
          )}
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5 w-full">
          {transaction.category && (
            <span className="truncate">{transaction.category}</span>
          )}
          {(transaction.supplier || transaction.client) && (
            <>
              {transaction.category && <span>•</span>}
              <span className="truncate">
                {transaction.type === 'expense' ? transaction.supplier : transaction.client}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Valor + Ações à direita */}
      <div className="flex items-center justify-end gap-3">
        {/* Badge + Valor container */}
        <div className="flex items-center gap-2 min-w-[180px] justify-end">
          {/* Type Badge */}
          <Badge
            className={`
              ${transaction.type === 'income'
                ? 'bg-finance-green/10 text-finance-green border-finance-green/20'
                : 'bg-finance-red/10 text-finance-red border-finance-red/20'
              }
              font-medium
            `}
          >
            {transaction.type === 'income' ? 'Receita' : 'Despesa'}
          </Badge>

          {/* Valor sempre alinhado à direita */}
          <span className="font-medium text-sm text-right min-w-[100px] text-foreground">
            {new Intl.NumberFormat('pt-BR', {
              style: 'currency',
              currency: 'BRL'
            }).format(transaction.amount)}
          </span>
        </div>

        {/* Attachment badge (icon only) - Always rendered for alignment */}
        <Button
          variant="ghost"
          size="sm"
          className={`h-7 w-7 p-0 flex-shrink-0 ${!hasAttachmentsOptimistic ? 'invisible' : ''}`}
          onClick={stop}
          title={hasAttachmentsOptimistic ? "Possui anexos" : ""}
          disabled={!hasAttachmentsOptimistic}
        >
          <AttachmentIcon className="h-3.5 w-3.5 text-muted-foreground" />
        </Button>

        {/* Actions dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0 flex-shrink-0"
              onClick={stop}
              title="Ações"
            >
              <MoreVertical className="h-4 w-4 text-muted-foreground" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-40">
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation(); // Impede que o clique propague para a linha
              }}
              onSelect={(event) => {
                event.preventDefault();
                setTimeout(() => {
                  onEdit(transaction.id);
                }, 0);
              }}
              className="cursor-pointer"
            >
              <Edit className="h-4 w-4 mr-2 text-muted-foreground" />
              Editar
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation(); // Impede que o clique propague para a linha
              }}
              onSelect={(event) => {
                event.preventDefault();
                console.log('onSelect chamado para transação:', transaction.id);
                // Usar setTimeout para garantir que o dropdown feche antes de executar a ação
                setTimeout(() => {
                  onTogglePaid(transaction.id);
                }, 0);
              }}
              className="cursor-pointer"
            >
              <Check className="h-4 w-4 mr-2 text-muted-foreground" />
              {transaction.is_paid ? 'Desmarcar' : 'Marcar pago'}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
              }}
              onSelect={(event) => {
                event.preventDefault();
                setTimeout(() => {
                  onClone(transaction);
                }, 0);
              }}
              className="cursor-pointer"
            >
              <ArrowDownCircle className="h-4 w-4 mr-2 text-muted-foreground" />
              Clonar
            </DropdownMenuItem>
            {/* Only show receipt option for paid income transactions */}
            {transaction.type === 'income' && transaction.is_paid && (
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                }}
                onSelect={(event) => {
                  event.preventDefault();
                  onGenerateReceipt(transaction);
                }}
                className="cursor-pointer"
              >
                <Receipt className="h-4 w-4 mr-2 text-muted-foreground" />
                Enviar Recibo
              </DropdownMenuItem>
            )}
            {/* Only show bill option for pending income transactions */}
            {transaction.type === 'income' && !transaction.is_paid && (
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                }}
                onSelect={(event) => {
                  event.preventDefault();
                  onGenerateBill(transaction);
                }}
                className="cursor-pointer"
              >
                <Send className="h-4 w-4 mr-2 text-muted-foreground" />
                Enviar Cobrança
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation(); // Impede que o clique propague para a linha
              }}
              onSelect={(event) => {
                event.preventDefault();
                setTimeout(() => {
                  onDelete(transaction.id);
                }, 0);
              }}
              className="cursor-pointer text-destructive focus:text-destructive"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Excluir
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

export default function Transactions() {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [editingTransactionId, setEditingTransactionId] = useState<string | null>(null);
  const [selectedTransactions, setSelectedTransactions] = useState<Set<string>>(new Set());
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Batch operation modals state
  const [isDateModalOpen, setIsDateModalOpen] = useState(false);
  const [isAmountModalOpen, setIsAmountModalOpen] = useState(false);
  const [isAccountModalOpen, setIsAccountModalOpen] = useState(false);

  // Receipt modal state
  const [receiptModalOpen, setReceiptModalOpen] = useState(false);
  const [selectedTransactionForReceipt, setSelectedTransactionForReceipt] = useState<any | null>(null);

  // Bill modal state
  const [billModalOpen, setBillModalOpen] = useState(false);
  const [selectedTransactionForBill, setSelectedTransactionForBill] = useState<any | null>(null);

  const { type: routeType } = useParams<{ type: string }>();
  const transactionType = routeType === 'expenses' ? 'expense' : routeType === 'incomes' ? 'income' : undefined;

  const { transactions, isLoading, deleteTransaction, toggleTransactionPaid } = useTransactions({ type: transactionType });
  const [initialCloneTx, setInitialCloneTx] = useState<any | null>(null);

  const [activeCardFilter, setActiveCardFilter] = useState<import("@/hooks/useTransactionFilters").CardFilterType>(null);

  const {
    searchTerm,
    setSearchTerm,
    sortType,
    setSortType,
    periodType,
    setPeriodType,
    dateRange,
    setDateRange,
    appliedDateRange,
    setAppliedDateRange,
    filterTransactions,
  } = useTransactionFilters({ activeCardFilter });

  // Memoize filtered transactions with status calculation and sorting
  const filteredTransactions = useMemo(() => {
    if (!transactions) return [];

    const filteredByDate = filterTransactions(transactions);

    // Update all transactions with dynamic status calculation
    const withStatus = filteredByDate.map(transaction =>
      updateTransactionStatus(transaction)
    );

    // Aplicar filtro de status
    let filtered = statusFilter === 'all'
      ? withStatus
      : withStatus.filter(transaction => {
          // Map legacy status filters to new TransactionStatus values
          const mappedFilter = statusFilter === 'overdue' ? 'OVERDUE' :
                              statusFilter === 'paid' ? 'PAID' :
                              statusFilter === 'due-today' || statusFilter === 'due-soon' || statusFilter === 'future' ? 'PENDING' :
                              statusFilter;
          return transaction.status === mappedFilter;
        });

    // Aplicar ordenação
    filtered.sort((a, b) => {
      switch (sortType) {
        case "date-desc":
          return fromDateString(b.date).getTime() - fromDateString(a.date).getTime();
        case "date-asc":
          return fromDateString(a.date).getTime() - fromDateString(b.date).getTime();
        case "amount-desc":
          return b.amount - a.amount;
        case "amount-asc":
          return a.amount - b.amount;
        case "description-asc":
          return a.description.localeCompare(b.description);
        case "description-desc":
          return b.description.localeCompare(a.description);
        default:
          return 0;
      }
    });

    return filtered;
  }, [transactions, filterTransactions, statusFilter, sortType]);

  // Calcular resumo usando filteredTransactions (já filtrado por data e status)
  const summary = useMemo(() => {
    if (!filteredTransactions || filteredTransactions.length === 0) {
      return {
        total: 0,
        pending: 0,
        overdue: 0,
        dueToday: 0,
        dueSoon: 0,
        paid: 0,
        pendingCount: 0,
        overdueCount: 0,
        dueTodayCount: 0,
        dueSoonCount: 0,
        paidCount: 0
      };
    }

    const total = filteredTransactions.reduce((sum, t) => sum + t.amount, 0);
    const pending = filteredTransactions.filter(t => !t.is_paid).reduce((sum, t) => sum + t.amount, 0);
    const overdue = filteredTransactions.filter(t => t.status === 'overdue').reduce((sum, t) => sum + t.amount, 0);
    const dueToday = filteredTransactions.filter(t => t.status === 'due-today').reduce((sum, t) => sum + t.amount, 0);
    const dueSoon = filteredTransactions.filter(t => t.status === 'due-soon').reduce((sum, t) => sum + t.amount, 0);
    const paid = filteredTransactions.filter(t => t.status === 'paid').reduce((sum, t) => sum + t.amount, 0);

    return {
      total,
      pending,
      overdue,
      dueToday,
      dueSoon,
      paid,
      pendingCount: filteredTransactions.filter(t => !t.is_paid).length,
      overdueCount: filteredTransactions.filter(t => t.status === 'overdue').length,
      dueTodayCount: filteredTransactions.filter(t => t.status === 'due-today').length,
      dueSoonCount: filteredTransactions.filter(t => t.status === 'due-soon').length,
      paidCount: filteredTransactions.filter(t => t.status === 'paid').length
    };
  }, [filteredTransactions]);

  const getPageTitle = () => {
    if (transactionType === 'expense') return "Contas a Pagar";
    if (transactionType === 'income') return "Contas a Receber";
    return "Transações";
  };

  const getPageIcon = () => {
    if (transactionType === 'expense') return <ArrowDownCircle className="h-8 w-8 text-finance-red" />;
    if (transactionType === 'income') return <ArrowUpCircle className="h-8 w-8 text-finance-green" />;
    return <Wallet className="h-8 w-8 text-secondary" />;
  };

  const getPageDescription = () => {
    if (transactionType === 'expense') {
      return "Gerencie suas obrigações financeiras, acompanhe vencimentos e organize seus pagamentos";
    }
    if (transactionType === 'income') {
      return "Acompanhe seus recebíveis, monitore cobranças e mantenha seu fluxo de caixa saudável";
    }
    return "Gerencie todas as suas transações financeiras em um único lugar";
  };

  const handleSelectTransaction = useCallback((transactionId: string) => {
    setSelectedTransactions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(transactionId)) {
        newSet.delete(transactionId);
      } else {
        newSet.add(transactionId);
      }
      return newSet;
    });
  }, []);

  const handleSelectAllTransactions = useCallback((checked: boolean) => {
    if (checked) {
      filteredTransactions.forEach(t => handleSelectTransaction(t.id));
    } else {
      selectedTransactions.forEach(id => handleSelectTransaction(id));
    }
  }, [filteredTransactions, selectedTransactions, handleSelectTransaction]);

  const handleMarkAsPaid = useCallback(async (transactionId: string) => {
    console.log('handleMarkAsPaid chamado para:', transactionId);
    try {
      console.log('Chamando toggleTransactionPaid...');
      await toggleTransactionPaid.mutateAsync(transactionId);
      console.log('toggleTransactionPaid executado com sucesso');
      toast.success('Transação atualizada com sucesso');
    } catch (error) {
      console.error('Erro em handleMarkAsPaid:', error);
      toast.error('Erro ao atualizar transação');
    }
  }, [toggleTransactionPaid]);

  const handleDeleteSelected = useCallback(async () => {
    if (selectedTransactions.size === 0) return;

    if (window.confirm(`Tem certeza que deseja excluir ${selectedTransactions.size} transação(ões) selecionada(s)?`)) {
      try {
        const deletePromises = Array.from(selectedTransactions).map(id =>
          deleteTransaction.mutateAsync(id)
        );
        await Promise.all(deletePromises);
        setSelectedTransactions(new Set());
        toast.success('Transações excluídas com sucesso');
      } catch (error) {
        toast.error('Erro ao excluir transações');
      }
    }
  }, [selectedTransactions, deleteTransaction]);

  const handleExport = useCallback(() => {
    if (!transactions || transactions.length === 0) {
      toast.error('Nenhuma transação para exportar');
      return;
    }

    const filteredData = filteredTransactions;
    const headers = ["Data", "Descrição", "Categoria", "Valor", "Tipo", "Status"];
    const rows = filteredData.map(transaction => [
      formatBR(transaction.date),
      `"${transaction.description.replace(/"/g, '""')}"`,
      `"${transaction.category.replace(/"/g, '""')}"`,
      transaction.amount.toFixed(2),
      transaction.type === 'income' ? 'Receita' : 'Despesa',
      transaction.is_paid ? 'Pago' : 'Pendente'
    ].join(','));

    const csvContent = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `transacoes_${transactionType || 'all'}_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    setTimeout(() => URL.revokeObjectURL(link.href), 100);
    toast.success('Arquivo exportado com sucesso');
  }, [transactions, filteredTransactions, transactionType]);

  const handleTransactionClick = useCallback((transactionId: string) => {
    setEditingTransactionId(transactionId);
    setIsDialogOpen(true);
  }, []);

  const handleGenerateReceipt = useCallback((transaction: any) => {
    setSelectedTransactionForReceipt(transaction);
    setReceiptModalOpen(true);
  }, []);

  const handleGenerateBill = useCallback((transaction: any) => {
    setSelectedTransactionForBill(transaction);
    setBillModalOpen(true);
  }, []);

  // Memoize selection state for optimized list
  const isAllSelected = useMemo(() => {
    return filteredTransactions.length > 0 && selectedTransactions.size === filteredTransactions.length;
  }, [filteredTransactions, selectedTransactions]);

  useEffect(() => {
    const handleOpenTransactionDialog = () => {
      setIsDialogOpen(true);
      setEditingTransactionId(null);
    };

    window.addEventListener("openNewTransactionDialog", handleOpenTransactionDialog);

    return () => {
      window.removeEventListener("openNewTransactionDialog", handleOpenTransactionDialog);
    };
  }, []);

  return (
    <MainLayout>
      <SEO
        title={getPageTitle()}
        description={getPageDescription()}
        keywords={`${getPageTitle().toLowerCase()}, transações financeiras, gestão financeira, fluxo de caixa`}
      />
      <div className="space-y-6">
  

        {/* Summary Cards */}
        <TransactionSummaryCards
          transactions={transactions}
          appliedDateRange={appliedDateRange}
          transactionType={transactionType}
          onCardClick={(filterType) => {
            setActiveCardFilter(prev => prev === filterType ? null : filterType);
          }}
          activeCardFilter={activeCardFilter ?? 'total'}
        />

  
        {/* Main Content */}
        <div className="space-y-4">

  
          {/* Filters and Actions Bar */}
          <div className="section-delimited pt-6">
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
              <div className="flex flex-col sm:flex-row gap-3 flex-1 w-full sm:w-auto items-start sm:items-center">
                {/* Date Filter */}
                <VencimentoDateFilter
                  periodType={periodType}
                  setPeriodType={setPeriodType}
                  dateRange={dateRange}
                  setDateRange={setDateRange}
                  appliedDateRange={appliedDateRange}
                  setAppliedDateRange={setAppliedDateRange}
                />

                {/* Search */}
                <div className="relative flex-1 sm:flex-initial">
                  <div className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none">
                    🔍
                  </div>
                  <input
                    type="text"
                    placeholder={`Buscar ${getPageTitle().toLowerCase()}...`}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-10 py-2 border border-input bg-background rounded-md text-sm input-focus-internal w-full sm:w-64"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm('')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      title="Limpar busca"
                      type="button"
                    >
                      ✕
                    </button>
                  )}
                </div>

                {/* Sort */}
                <select
                  value={sortType}
                  onChange={(e) => setSortType(e.target.value)}
                  className="px-3 py-2 border border-input bg-background rounded-md text-sm input-focus-internal min-w-[180px]"
                >
                  <option value="">Ordenar por...</option>
                  <option value="date-desc">Data (Mais recente)</option>
                  <option value="date-asc">Data (Mais antiga)</option>
                  <option value="amount-desc">Valor (Maior)</option>
                  <option value="amount-asc">Valor (Menor)</option>
                  <option value="description-asc">Descrição (A-Z)</option>
                  <option value="description-desc">Descrição (Z-A)</option>
                </select>
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      size="sm"
                      className="bg-accent text-accent-foreground hover:bg-accent/90"
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      Relatórios
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-64">
                    <DropdownMenuItem
                      onClick={() => {
                        // TODO: Implementar navegação para gráfico de despesas
                        toast.info("Gráfico de despesas em desenvolvimento");
                      }}
                    >
                      <TrendingUp className="h-4 w-4 mr-2" />
                      Gráfico de despesas
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => {
                        // TODO: Implementar navegação para gráfico de principais despesas
                        toast.info("Gráfico das principais despesas em desenvolvimento");
                      }}
                    >
                      <DollarSign className="h-4 w-4 mr-2" />
                      Gráfico das principais despesas por mês
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => {
                        // TODO: Implementar navegação para análise de vencimentos
                        toast.info("Análise de vencimentos em desenvolvimento");
                      }}
                    >
                      <Calendar className="h-4 w-4 mr-2" />
                      Análise de vencimentos da semana
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsImportDialogOpen(true)}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Importar Planilha
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExport}
                  disabled={isLoading}
                >
                  Exportar
                </Button>
              </div>
            </div>

            {/* Batch Actions */}
            {selectedTransactions.size > 0 && (
              <div className="flex items-center justify-between p-3 bg-accent/10 border border-primary/30 rounded-lg mt-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-primary">
                    {selectedTransactions.size} transação(ões) selecionada(s)
                  </span>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedTransactions(new Set())}
                    className="text-primary border-primary hover:bg-primary/10"
                  >
                    Limpar seleção
                  </Button>
                  <BatchActionsDropdown
                    selectedTransactions={selectedTransactions}
                    onClearSelection={() => setSelectedTransactions(new Set())}
                    onOpenDateModal={() => setIsDateModalOpen(true)}
                    onOpenAmountModal={() => setIsAmountModalOpen(true)}
                    onOpenAccountModal={() => setIsAccountModalOpen(true)}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Transactions List */}
          <div className="section-delimited-darker">
            {filterTransactions(transactions).length === 0 ? (
              <Card className="card-elevated border-0">
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                    {getPageIcon()}
                  </div>
                  <h3 className="text-lg font-semibold mb-2">
                    Nenhuma {getPageTitle().toLowerCase()} encontrada
                  </h3>
                  <p className="text-sm text-muted-foreground text-center max-w-sm">
                    {isLoading
                      ? 'Carregando...'
                      : 'Adicione novas transações ou ajuste os filtros.'
                    }
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-1">
                {/* Header */}
                <div
                  className="grid gap-2 px-4 py-3 text-sm font-medium text-muted-foreground bg-muted rounded-t-lg grid-cols-transaction-row"
                >
                  <div className="flex justify-center">
                    <input
                      type="checkbox"
                      checked={selectedTransactions.size === filteredTransactions.length && filteredTransactions.length > 0}
                      onChange={(e) => {
                        if (e.target.checked) {
                          filteredTransactions.forEach(t => handleSelectTransaction(t.id));
                        } else {
                          selectedTransactions.forEach(id => handleSelectTransaction(id));
                        }
                      }}
                    />
                  </div>
                  <SortableHeader
                    sortKey="status"
                    currentSort={sortType}
                    onSort={setSortType}
                    className="justify-center"
                  >
                    Status
                  </SortableHeader>
                  <SortableHeader
                    sortKey="date"
                    currentSort={sortType}
                    onSort={setSortType}
                  >
                    {transactionType === 'income' ? 'Vencimento' : 'Data'}
                  </SortableHeader>
                  <SortableHeader
                    sortKey="description"
                    currentSort={sortType}
                    onSort={setSortType}
                  >
                    Descrição
                  </SortableHeader>
                  <SortableHeader
                    sortKey="amount"
                    currentSort={sortType}
                    onSort={setSortType}
                    className="justify-end pr-20"
                  >
                    Valor
                  </SortableHeader>
                </div>

                {/* Transaction Rows */}
                {filteredTransactions.map((transaction) => (
                  <GridTransactionRow
                    key={transaction.id}
                    transaction={transaction}
                    isSelected={selectedTransactions.has(transaction.id)}
                    onSelect={handleSelectTransaction}
                    onEdit={handleTransactionClick}
                    onTogglePaid={handleMarkAsPaid}
                    onGenerateReceipt={handleGenerateReceipt}
                    onGenerateBill={handleGenerateBill}
                    allTransactions={filteredTransactions}
                    onClone={(tx) => {
                      setInitialCloneTx(tx);
                      setEditingTransactionId(null);
                      setIsDialogOpen(true);
                    }}
                    onDelete={async (id) => {
                      if (window.confirm('Tem certeza que deseja excluir esta transação?')) {
                        try {
                          await deleteTransaction.mutateAsync(id);
                          toast.success('Transação excluída com sucesso');
                        } catch (error) {
                          toast.error('Erro ao excluir transação');
                        }
                      }
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        <TransactionFormContainer
          isOpen={isDialogOpen}
          onOpenChange={setIsDialogOpen}
          transactionId={editingTransactionId}
          initialTransaction={initialCloneTx}
          onClose={() => {
            setIsDialogOpen(false);
            setEditingTransactionId(null);
          }}
        />

        {/* Batch Operation Modals */}
        <BatchDatePickerModal
          open={isDateModalOpen}
          onOpenChange={setIsDateModalOpen}
          selectedTransactions={selectedTransactions}
          onClearSelection={() => setSelectedTransactions(new Set())}
        />
        <BatchAmountModal
          open={isAmountModalOpen}
          onOpenChange={setIsAmountModalOpen}
          selectedTransactions={selectedTransactions}
          onClearSelection={() => setSelectedTransactions(new Set())}
        />
        <BatchAccountSelectModal
          open={isAccountModalOpen}
          onOpenChange={setIsAccountModalOpen}
          selectedTransactions={selectedTransactions}
          onClearSelection={() => setSelectedTransactions(new Set())}
        />

        {/* Import Spreadsheet Dialog */}
        <ImportSpreadsheetDialog
          open={isImportDialogOpen}
          onOpenChange={setIsImportDialogOpen}
        />

        {/* Receipt Modal */}
        {selectedTransactionForReceipt && (
          <ReceiptModal
            open={receiptModalOpen}
            onOpenChange={setReceiptModalOpen}
            transaction={selectedTransactionForReceipt}
            client={selectedTransactionForReceipt.client_id ? {
              name: selectedTransactionForReceipt.client_name || 'Cliente',
              email: selectedTransactionForReceipt.client_email,
              phone: selectedTransactionForReceipt.client_phone
            } : undefined}
          />
        )}

        {/* Bill Modal */}
        {selectedTransactionForBill && (
          <BillModal
            open={billModalOpen}
            onOpenChange={setBillModalOpen}
            transaction={selectedTransactionForBill}
            client={selectedTransactionForBill.client_id ? {
              name: selectedTransactionForBill.client_name || 'Cliente',
              email: selectedTransactionForBill.client_email,
              phone: selectedTransactionForBill.client_phone,
              documentNumber: selectedTransactionForBill.client_document
            } : undefined}
            bankDetails={{
              bank: selectedTransactionForBill.bank_name,
              agency: selectedTransactionForBill.bank_agency,
              account: selectedTransactionForBill.bank_account,
              pixKey: selectedTransactionForBill.pix_key
            }}
          />
        )}
      </div>
    </MainLayout>
  );
}
